/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import React from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';

import {
  Button,
  Dimmer,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Segment,
  Sidebar,
  Table,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { getInseePacsLot } from '@/routes';
import { getFichePacsPath } from '@/utils';
import type {
  ApercuLotInsee,
  PacsATransferer,
  ResultatTransfertInsee,
  TypeDeclarationInsee,
} from '@/types';

import { stylesCorps } from './styles';
import type { VueInsee } from './types';

type CouleurLabel = React.ComponentProps<typeof Label>['color'];

/** Statut SDFI d'un lot réceptionné par l'INSEE */
const STATUT_TRAITE = 99;

const TYPES: Record<
  TypeDeclarationInsee,
  { libelle: string; couleur: CouleurLabel }
> = {
  initiale: { libelle: 'Déclaration initiale', couleur: 'blue' },
  dissolution: { libelle: 'Dissolution', couleur: 'purple' },
  irrecevabilite: { libelle: 'Irrecevable', couleur: 'orange' },
};

/** Bouton présenté comme un lien Semantic */
const STYLE_LIEN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#4183c4',
  cursor: 'pointer',
};

const libellePartenaire = (
  partenaire: PacsATransferer['partenaires'][number],
) => {
  const civilite = { M: 'M.', F: 'Mme' }[partenaire.sexe] ?? '';
  return _.trim(
    `${civilite} ${_.toUpper(partenaire.nomFamille)} ${_.toUpper(partenaire.prenoms)}`,
  );
};

const libellePartenaires = (pacs: PacsATransferer) =>
  _.map(pacs.partenaires, libellePartenaire).join(' et ');

const libelleGroupe = (pacs: PacsATransferer) =>
  pacs.registre.annee
    ? `${pacs.registre.annee} - Registre n°${pacs.registre.numero ?? '?'} des PACS`
    : pacs.registre.libelle || 'Registre inconnu';

const pluriel = (nombre: number, singulier: string, plurielLibelle: string) =>
  `${nombre} ${nombre > 1 ? plurielLibelle : singulier}`;

interface InseeTransfertProps {
  /** Contrôle relancé à chaque activation (ouverture de la modale, affichage de la page) */
  actif: boolean;
  /** Page du menu : le tableau occupe toute la hauteur */
  pleinePage?: boolean;
  /** Appelé après un envoi, pour rafraîchir le compteur du tableau de bord */
  onTransfert?: () => void;
  /** Mise en page : modale du tableau de bord ou page du menu */
  children: (vue: VueInsee) => React.ReactElement;
}

/**
 * PACS en attente de transfert à l'INSEE : contrôle de chaque PACS avant envoi
 * (anomalies bloquantes), puis dépôt du lot sur le SDFI de l'INSEE.
 */
const InseeTransfert = ({
  actif: open,
  pleinePage = false,
  onTransfert,
  children: mettreEnPage,
}: InseeTransfertProps) => {
  const styles = stylesCorps(pleinePage);
  const history = useHistory();

  const [apercu, setApercu] = React.useState<ApercuLotInsee | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [envoi, setEnvoi] = React.useState(false);
  const [pacsAnomalies, setPacsAnomalies] =
    React.useState<PacsATransferer | null>(null);
  const [panneauOuvert, setPanneauOuvert] = React.useState(false);
  const [groupesReplies, setGroupesReplies] = React.useState<string[]>([]);

  const charger = React.useCallback(() => {
    setLoading(true);
    request
      .get(getInseePacsLot())
      .then((reponse: any) => setApercu(reponse as ApercuLotInsee))
      .catch(() => {
        toast.error(
          'Une erreur est survenue lors de la préparation du transfert INSEE',
        );
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    if (open) {
      setPanneauOuvert(false);
      charger();
    }
  }, [open, charger]);

  const groupes = React.useMemo(() => {
    const parRegistre = _.groupBy(apercu?.pacs ?? [], libelleGroupe);
    return _.orderBy(
      _.map(parRegistre, (pacs, libelle) => ({
        libelle,
        pacs,
        annee: pacs[0].registre.annee ?? 0,
      })),
      ['annee', 'libelle'],
      ['desc', 'desc'],
    );
  }, [apercu]);

  const nombrePacsEnAnomalie = _.filter(
    apercu?.pacs,
    (pacs) => !_.isEmpty(pacs.anomalies),
  ).length;

  const ouvrirAnomalies = React.useCallback((pacs: PacsATransferer) => {
    setPacsAnomalies(pacs);
    setPanneauOuvert(true);
  }, []);

  const basculerGroupe = React.useCallback((libelle: string) => {
    setGroupesReplies((prev) => _.xor(prev, [libelle]));
  }, []);

  /**
   * Fiche du PACS dans un nouvel onglet, sur la convention en anomalie : la modale reste
   * ouverte et se recharge au retour sur l'onglet.
   */
  const corrigerDansLaFiche = React.useCallback(
    (pacs: PacsATransferer) => {
      const lien = history.createHref({
        pathname: getFichePacsPath(pacs.identifiantPacs),
        search: `?convention=${pacs.identifiantConvention}`,
      });
      window.open(lien, '_blank', 'noopener');
    },
    [history],
  );

  // Retour sur l'onglet après correction : contrôle relancé
  React.useEffect(() => {
    if (!open) return undefined;

    const recharger = () => {
      if (document.visibilityState === 'visible') charger();
    };
    document.addEventListener('visibilitychange', recharger);

    return () => document.removeEventListener('visibilitychange', recharger);
  }, [open, charger]);

  const transmettre = React.useCallback(() => {
    setEnvoi(true);
    request
      .post(getInseePacsLot())
      .then((reponse: any) => {
        const resultat = reponse as ResultatTransfertInsee;
        if (resultat.statut === STATUT_TRAITE) {
          toast.success(
            `Lot n°${resultat.numero} réceptionné par l'INSEE (${pluriel(resultat.nombreBulletins ?? 0, 'bulletin', 'bulletins')}).`,
          );
        } else {
          toast.warning(resultat.message, { autoClose: false });
        }
      })
      .catch((error: any) => {
        const message = error?.response?.data?.message ?? error?.data?.message;
        toast.error(
          message ?? "Une erreur est survenue lors du transfert à l'INSEE",
          { autoClose: false },
        );
      })
      .finally(() => {
        setEnvoi(false);
        onTransfert?.();
        charger();
      });
  }, [charger, onTransfert]);

  const messagePied = React.useMemo(() => {
    if (!apercu) return null;
    if (!_.isEmpty(apercu.anomaliesGenerales)) {
      return `Paramètres INSEE de la collectivité incomplets : ${_.map(apercu.anomaliesGenerales, 'message').join(' ')}`;
    }
    if (nombrePacsEnAnomalie > 0) {
      return nombrePacsEnAnomalie > 1
        ? `${nombrePacsEnAnomalie} PACS présentent une anomalie : corrigez-les pour pouvoir transmettre à l'INSEE.`
        : "1 PACS présente une anomalie : corrigez-la pour pouvoir transmettre à l'INSEE.";
    }
    if (!_.isEmpty(apercu.erreursXsd)) {
      return "Le fichier généré n'est pas conforme au schéma de l'INSEE : contactez l'éditeur.";
    }
    return null;
  }, [apercu, nombrePacsEnAnomalie]);

  const renderControle = (pacs: PacsATransferer) => {
    if (_.isEmpty(pacs.anomalies)) {
      return (
        <Label color="green" size="small" basic>
          Transférable
        </Label>
      );
    }

    return (
      <>
        <Label
          as="a"
          color="red"
          size="small"
          basic
          onClick={() => ouvrirAnomalies(pacs)}
          data-testid={`insee-transfert-anomalies-${pacs.identifiantConvention}`}
        >
          {pluriel(pacs.anomalies.length, 'anomalie', 'anomalies')}
        </Label>
        <div style={{ marginTop: '0.3em', fontSize: '0.9em', color: 'grey' }}>
          {pacs.anomalies[0]}
          {pacs.anomalies.length > 1 && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => ouvrirAnomalies(pacs)}
                style={STYLE_LIEN}
              >
                {`et ${pluriel(pacs.anomalies.length - 1, 'autre', 'autres')}`}
              </button>
            </>
          )}
        </div>
      </>
    );
  };

  const renderTableau = () => {
    if (_.isEmpty(apercu?.pacs)) {
      return (
        <Message
          info
          icon="info circle"
          content="Aucun PACS en attente de transfert à l'INSEE."
        />
      );
    }

    return (
      <div style={styles.tableau}>
        <Table celled compact data-testid="insee-transfert-tableau">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Type de déclaration</Table.HeaderCell>
              <Table.HeaderCell>Numéro du PACS</Table.HeaderCell>
              <Table.HeaderCell>Date d'enregistrement</Table.HeaderCell>
              <Table.HeaderCell>Partenaires</Table.HeaderCell>
              <Table.HeaderCell>Contrôle avant transfert</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {groupes.map((groupe) => {
              const replie = _.includes(groupesReplies, groupe.libelle);
              return (
                <React.Fragment key={groupe.libelle}>
                  <Table.Row active>
                    <Table.Cell
                      colSpan={5}
                      style={{ cursor: 'pointer', fontWeight: 700 }}
                      onClick={() => basculerGroupe(groupe.libelle)}
                    >
                      <Icon name={replie ? 'chevron right' : 'chevron down'} />
                      {groupe.libelle}
                      <Label
                        circular
                        basic
                        size="small"
                        style={{ marginLeft: '0.6em' }}
                      >
                        {groupe.pacs.length}
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                  {!replie &&
                    groupe.pacs.map((pacs) => (
                      <Table.Row
                        key={pacs.identifiantConvention}
                        data-testid={`insee-transfert-ligne-${pacs.identifiantConvention}`}
                      >
                        <Table.Cell>
                          <Label
                            color={TYPES[pacs.type].couleur}
                            size="small"
                            basic
                          >
                            {TYPES[pacs.type].libelle}
                          </Label>
                        </Table.Cell>
                        <Table.Cell>{pacs.numero}</Table.Cell>
                        <Table.Cell>
                          {pacs.date
                            ? dayjs(pacs.date).format('DD/MM/YYYY')
                            : ''}
                        </Table.Cell>
                        <Table.Cell style={{ fontWeight: 600 }}>
                          {libellePartenaires(pacs)}
                        </Table.Cell>
                        <Table.Cell>{renderControle(pacs)}</Table.Cell>
                      </Table.Row>
                    ))}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const renderPanneauAnomalies = () => (
    <Sidebar
      as={Segment}
      animation="overlay"
      direction="right"
      width="wide"
      visible={panneauOuvert}
      onHide={() => setPanneauOuvert(false)}
      data-testid="insee-transfert-panneau-anomalies"
      style={{
        margin: 0,
        padding: '1.5em',
        border: 'none',
        borderRadius: 0,
        backgroundColor: '#f7f7f8',
        boxShadow: '-4px 0 12px rgba(34, 36, 38, 0.12)',
      }}
    >
      {pacsAnomalies && (
        <>
          <Icon
            name="close"
            link
            aria-label="Fermer"
            onClick={() => setPanneauOuvert(false)}
            style={{ position: 'absolute', top: '1em', right: '1em' }}
          />
          <Header as="h3" style={{ marginRight: '1.5em' }}>
            {`${pluriel(pacsAnomalies.anomalies.length, 'anomalie bloque', 'anomalies bloquent')} le transfert`}
            <Header.Subheader>
              {`${libellePartenaires(pacsAnomalies)} · N° ${pacsAnomalies.numero}`}
            </Header.Subheader>
          </Header>
          {pacsAnomalies.anomalies.map((anomalie) => (
            <Segment
              key={anomalie}
              style={{ display: 'flex', gap: '0.5em', margin: '0 0 0.75em' }}
            >
              <Icon name="warning circle" color="red" />
              <div>
                <div>{anomalie}</div>
                <button
                  type="button"
                  onClick={() => corrigerDansLaFiche(pacsAnomalies)}
                  style={{ ...STYLE_LIEN, marginTop: '0.5em' }}
                >
                  Corriger dans la fiche
                </button>
              </div>
            </Segment>
          ))}
        </>
      )}
    </Sidebar>
  );

  return mettreEnPage({
    titre: "PACS en attente de transfert à l'INSEE",
    corps: (
      // Le panneau des anomalies glisse par-dessus le tableau, depuis la droite
      <Sidebar.Pushable style={styles.pushable}>
        {renderPanneauAnomalies()}
        <Sidebar.Pusher style={styles.pusher}>
          {loading && !apercu ? (
            <Dimmer active inverted>
              <Loader content="Contrôle des PACS en cours..." />
            </Dimmer>
          ) : (
            renderTableau()
          )}
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    ),
    message: messagePied && (
      <Message
        negative
        size="small"
        compact
        content={messagePied}
        data-testid="insee-transfert-message"
      />
    ),
    boutons: (
      <Button
        primary
        size="small"
        icon="send"
        content="Transmettre à l'INSEE"
        loading={envoi}
        disabled={!apercu?.envoyable || envoi || loading}
        onClick={transmettre}
        data-testid="insee-transfert-transmettre"
      />
    ),
  });
};

export default InseeTransfert;
